import { trpc } from "@/lib/trpc";
import { BellOff, BellRing, LoaderCircle } from "lucide-react";
import React, { useEffect, useState } from "react";
import { DropdownMenuItem } from "./ui/dropdown-menu";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const PushNotificationManager: React.FC = () => {
    const { t } = useTranslation();
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [subscription, setSubscription] = useState<PushSubscription | null>(null);
    const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);

    const { data: vapidData, isLoading } = trpc.pushNotification.getPublicKey.useQuery();
    const subscribeMutation = trpc.pushNotification.subscribe.useMutation();
    const unsubscribeMutation = trpc.pushNotification.unsubscribe.useMutation();
    // const testNotificationMutation = trpc.pushNotification.testNotification.useMutation();

    // Get access to the service worker registration instead of registering a new one
    useEffect(() => {
        if ("serviceWorker" in navigator && "PushManager" in window) {
            // Instead of registering a new service worker, get the existing one
            navigator.serviceWorker.ready
                .then((reg) => {
                    setRegistration(reg);
                    console.log("Service Worker is ready:", reg);

                    // Check if already subscribed
                    return reg.pushManager.getSubscription();
                })
                .then((subscription) => {
                    if (subscription) {
                        setSubscription(subscription);
                        setIsSubscribed(true);
                        console.log("User is already subscribed to push notifications");
                    } else {
                        console.log("User is not subscribed to push notifications");
                    }
                })
                .catch((error) => {
                    console.error("Service Worker access failed:", error);
                });
        }
    }, []);

    // Subscribe to push notifications
    const subscribeUser = async () => {
        if (!registration || !vapidData) return;

        try {
            // Request notification permission first
            const permission = await Notification.requestPermission();
            if (permission !== "granted") {
                console.error("Notification permission denied");
                return;
            }

            console.log("Subscribing to push notifications with key:", vapidData.publicKey);
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(vapidData.publicKey || ""),
            });

            console.log("User subscribed to push notifications:", subscription);

            // Send subscription to server
            subscribeMutation.mutate({
                endpoint: subscription.endpoint,
                expirationTime: subscription.expirationTime || null,
                keys: {
                    p256dh: btoa(
                        String.fromCharCode.apply(null, [
                            ...new Uint8Array(subscription.getKey("p256dh") as ArrayBuffer),
                        ])
                    ),
                    auth: btoa(
                        String.fromCharCode.apply(null, [...new Uint8Array(subscription.getKey("auth") as ArrayBuffer)])
                    ),
                },
            }, {
                onSuccess: () => {
                    setSubscription(subscription);
                    setIsSubscribed(true);
                },
                onError: (error) => {
                    console.error("Failed to send subscription to server:", error);
                }
            });
        } catch (error) {
            console.error("Failed to subscribe to push notifications:", error);
        }
    };

    // Unsubscribe from push notifications
    const unsubscribeUser = async () => {
        if (!subscription) return;

        try {
            await subscription.unsubscribe();
            console.log("User unsubscribed from push notifications");

            // Send unsubscribe to server
            unsubscribeMutation.mutate({
                endpoint: subscription.endpoint,
            }, {
                onSuccess: () => {
                    setSubscription(null);
                    setIsSubscribed(false);
                },
                onError: (error) => {
                    console.error("Failed to send unsubscribe to server:", error);
                }
            });
        } catch (error) {
            console.error("Failed to unsubscribe from push notifications:", error);
        }
    };

    // Convert base64 to Uint8Array for applicationServerKey
    const urlBase64ToUint8Array = (base64String: string) => {
        const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
        const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }

        return outputArray;
    };

    // Only render if push is supported
    if (!("serviceWorker" in navigator && "PushManager" in window)) {
        return <div>{t("userMenu.pushNotSupported")}</div>;
    }

    const handleEnablePushNotification = () => {
        if (isSubscribed) {
            unsubscribeUser();
            toast.error(t("userMenu.pushDisabled"));
        } else {
            subscribeUser();
            toast.success(t("userMenu.pushEnabled"));
        }
    };

    return (
        <DropdownMenuItem onClick={handleEnablePushNotification}>
            <div className="flex w-full justify-between">
                <span>{t("userMenu.pushNotifications")}</span>
                {isLoading ? (
                    <LoaderCircle className="h-[1.2rem] w-[1.2rem] animate-spin" />
                ) : isSubscribed ? (
                    <BellRing className="h-[1.2rem] w-[1.2rem]" />
                ) : (
                    <BellOff className="h-[1.2rem] w-[1.2rem]" />
                )}
            </div>
        </DropdownMenuItem>
    );
};

export default PushNotificationManager;
